import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Search, XCircle } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { Errors } from '../helpers';
import GetEInvoicingRecipientsQuery from './GetEInvoicingRecipientsQuery';
import UI from '../UI';
import useLazyQuery from '../../api/useLazyQuery';

export interface EInvoicingRecipient {
  address: string;
  name: string;
}

interface EInvoicingRecipientPickerProps {
  value: string | null;
  vatId?: string | null;
  companyName?: string | null;
  onChange: (address: string | null) => void;
  onBlur?: () => void;
  errors?: Errors | null;
  touched?: any;
}

const EInvoicingRecipientPicker = ({
  value, vatId, companyName, onChange, onBlur, errors, touched,
}: EInvoicingRecipientPickerProps) => {
  const { t } = useTranslation('common');

  const [showPicker, setShowPicker] = useState(!!value);

  const toggleEInvoicingRecipientPicker = () => {
    if (showPicker) {
      onChange(null);
    }

    setShowPicker(!showPicker);
  };

  const [search, setSearch] = useState(value);
  const [recipients, setRecipients] = useState<EInvoicingRecipient[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedRecipient = value ? recipients.find((recipient) => recipient.address === value) : null;

  const [getRecipients, { loading }] = useLazyQuery(GetEInvoicingRecipientsQuery);

  const doSearch = useCallback((search: string[]) => {
    const filteredSearch = search.filter((value) => !!value);

    if (!filteredSearch.length) {
      return;
    }

    getRecipients({
      variables: {
        input: {
          search: filteredSearch,
        },
      },
    }).then((response) => {
      if (response.data?.eInvoicingRecipients) {
        setRecipients(response.data.eInvoicingRecipients);
      }
    });
  }, [getRecipients]);

  // Fetch recipients.
  useEffect(() => {
    if (search) {
      doSearch([search]);
    } else if (vatId || companyName) {
      doSearch([vatId, companyName]);
    }
  }, [search, vatId, companyName, doSearch]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;

    onChange(null);
    setSearch(query);

    if (!query) {
      // Search field cleared: reset results so the auto-search via vatId/companyName takes over.
      setRecipients([]);
    }
  };

  const handleSelectRecipient = (recipient: EInvoicingRecipient) => {
    onChange(recipient.address);

    if (!search) {
      setSearch(recipient.address);

      if (inputRef.current) {
        inputRef.current.value = recipient.address;
      }
    }
  };

  const handleDeselectRecipient = (recipient?: EInvoicingRecipient) => {
    onChange(null);

    if (recipient && search === recipient.address) {
      setSearch('');
      setRecipients([]);

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleReset = () => {
    onChange(null);
    setSearch('');
    setRecipients([]);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <UI.InputGroup sc={{ valid: !errors?.e_invoicing_address, touched: touched?.e_invoicing_address }}>
      <UI.InputLabel>
        {t('invoice_details.e_invoice')}
      </UI.InputLabel>
      {!selectedRecipient && (
        <UI.Checkbox
          checked={showPicker}
          onChange={toggleEInvoicingRecipientPicker}
        >
          {t('invoice_details.e_invoice_label')}
        </UI.Checkbox>
      )}
      {showPicker && (
        <>
          {!selectedRecipient && (
            <UI.InputWrapper sc={{ mt: 1 }}>
              <UI.Span>
                <UI.Icon>
                  {loading ? <UI.Loader /> : <Search />}
                </UI.Icon>
              </UI.Span>
              <UI.DebouncedInput
                value={search}
                onChange={handleSearch}
                onBlur={onBlur}
                placeholder={t('invoice_details.e_invoice_search_placeholder')}
                aria-label={t('invoice_details.e_invoice_search_placeholder')}
                sc={{ pl: 5 }}
                ref={inputRef}
              />
              {search && (
                <UI.Span>
                  <UI.A onClick={() => handleReset()} sc={{ color: 'gray.400' }}>
                    <UI.Icon>
                      <XCircle />
                    </UI.Icon>
                  </UI.A>
                </UI.Span>
              )}
            </UI.InputWrapper>
          )}
          {!loading && recipients.length === 0 && search && (
            <UI.Div sc={{ mt: 2, color: 'gray.500' }}>
              {t('invoice_details.e_invoice_no_results')}
            </UI.Div>
          )}
          {recipients.length > 0 && (
            <UI.GridContainer sc={{ gutter: 0.25, mt: 1 }}>
              {selectedRecipient && (
                <UI.Checkbox
                  key={selectedRecipient.address}
                  name="e_invoicing_recipient"
                  value={selectedRecipient.address}
                  checked
                  onChange={() => handleDeselectRecipient(selectedRecipient)}
                  sc={{ box: true }}
                >
                  <UI.Strong>
                    {selectedRecipient.name}
                  </UI.Strong>
                  <UI.Div>
                    {selectedRecipient.address}
                  </UI.Div>
                </UI.Checkbox>
              )}
              {!selectedRecipient && recipients.map((recipient) => (
                <UI.Radio
                  key={recipient.address}
                  name="e_invoicing_recipient"
                  value={recipient.address}
                  checked={value === recipient.address}
                  onChange={() => handleSelectRecipient(recipient)}
                  sc={{ box: true }}
                >
                  <UI.Strong>
                    {recipient.name}
                  </UI.Strong>
                  <UI.Div>
                    {recipient.address}
                  </UI.Div>
                </UI.Radio>
              ))}
            </UI.GridContainer>
          )}
          <UI.ErrorMessages attribute={t('invoice_details.e_invoice')} errors={errors?.e_invoicing_address} />
        </>
      )}
    </UI.InputGroup>
  );
};

export default EInvoicingRecipientPicker;
